import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { fetchGenericApi, } from '@zurich-framework/core/api';
import { FetchApiError, getErrorSource } from '@zurich-framework/core/errors';
import { useCallback } from 'react';
import { useBaseConfig, useBusinessProcessId } from '../../contexts';
import { useSnackbar, useTranslation } from '../../hooks';
import { useFetchApiOptions } from './useFetchApiOptions';
function startTimeout(timeoutMs, callback) {
    let clearCallback = null;
    const signalClear = () => {
        if (clearCallback)
            clearCallback();
    };
    const id = setTimeout(() => {
        // Call timeout callback which can return a clear callback as well
        clearCallback = callback();
    }, timeoutMs);
    return { id, signalClear };
}
function SnackbarTimeoutWarning({ warning, method, url, source, }) {
    return (_jsxs("div", { style: { display: 'block' }, children: [_jsx("div", { children: warning }, "warning_message"), (method || url) && (_jsx("div", { style: { fontSize: 10 }, children: `Request: ${method} ${source}:${url}` }, "warning_debug"))] }));
}
/**
 * Basic hook to fetch data from an API. It is the building block for useApiQuery and useApi.
 *
 * Important: This hook should only be used if useApiQuery and useApi are not suitable and
 *            you need fine-grained control of the fetch call.
 */
export function useFetchApi() {
    const { SnackbarError } = useFetchApiOptions();
    const businessProcessId = useBusinessProcessId();
    const { t } = useTranslation();
    const { close, stickLoading, stickError } = useSnackbar();
    const config = useBaseConfig();
    const { debug, getLogUrl } = config;
    const showApiError = useCallback(({ error, text, retryCallback, consoleOnly }) => {
        function showSnackbarError(message) {
            const snackbarKey = stickError({
                message,
                onClose: () => {
                    // on production stages, we invoke the retry callback, if available
                    if (!debug) {
                        void retryCallback?.();
                    }
                },
            });
            return snackbarKey;
        }
        if (debug) {
            console.error('🚨 useFetchApi Error:', { error, consoleOnly, debug });
        }
        // Only log error to console?
        if (consoleOnly) {
            return;
        }
        // Show snackbar
        const errorText = typeof text === 'function' ? text(error) : text;
        showSnackbarError(SnackbarError && debug ? (_jsx(SnackbarError, { error: error, text: errorText, businessProcessId: businessProcessId, logUrl: getLogUrl?.(businessProcessId) })) : (_jsxs("span", { children: [errorText, _jsx("br", {}), _jsx("b", { children: "Business Process Id: " }), " ", businessProcessId] })));
    }, [debug, getLogUrl, businessProcessId, stickError, SnackbarError]);
    const fetchApi = useCallback(async (request, params, { errorCallback = showApiError, timeoutWarningMs = 0, throwOnError, errorText, snackbar, finallyCallback, ...otherOptions }) => {
        const baseUrl = config.mocking.apiSources?.includes(request.source)
            ? `${config.externalApiSources['app']}/mock`
            : config.externalApiSources[request.source];
        // Start timeout, if requested
        const timeoutData = timeoutWarningMs > 0
            ? startTimeout(timeoutWarningMs, () => {
                // Handle timeout warning - just warn the user that it takes longer
                const warning = t('common:snackbar.warning.timeout');
                const snackbarKey = stickLoading({
                    message: config.debug
                        ? SnackbarTimeoutWarning({
                            warning,
                            method: request.method ?? 'GET',
                            url: request.route,
                            source: request.source,
                        })
                        : warning,
                });
                // Output in console as well
                console.warn(`API Timeout Warning: ${request.method ?? 'GET'} ${request.source}:${request.route}`);
                // Return with timeout clear callback
                return () => {
                    close(snackbarKey);
                };
            })
            : null;
        return fetchGenericApi({
            baseUrl,
            request,
            params,
            errorSource: getErrorSource('UseFetchApi'),
            options: {
                context: { businessProcessId, debug },
                ...otherOptions,
            },
        })
            .then((res) => res)
            .catch((error) => {
            if (throwOnError) {
                throw error;
            }
            // fetch cancel with abort signal?
            if (error instanceof Error && error.name === 'AbortError') {
                return null;
            }
            if (errorCallback && error instanceof FetchApiError) {
                errorCallback({
                    error,
                    consoleOnly: snackbar?.hideError ?? false,
                    text: errorText,
                });
            }
            return null;
        })
            .finally(() => {
            // Clear timeout, if available
            if (timeoutData) {
                clearTimeout(timeoutData.id);
                timeoutData.signalClear();
            }
            finallyCallback?.();
        });
    }, [
        businessProcessId,
        close,
        config.debug,
        config.externalApiSources,
        showApiError,
        stickLoading,
        t,
    ]);
    return { fetchApi, showApiError };
}
